import { type Ref, computed } from "vue";
import { BIO_GAS_CONTRACT_VERSIONS } from "@/app/fbs-hub-app/shared/@types/BioGasContractVersions";
import { FbsCustomerType } from "@/app/fbs-hub-app/shared/@types/FbsCustomerType";
import { MeterAssetType } from "@/app/fbs-hub-app/shared/@types/MeterAssetType";
import type { GasContainer } from "@/app/fbs-hub-app/shared/@types/GasContainer";
import type { Meter } from "@/app/fbs-hub-app/shared/@types/Meter";

export const useGetFbsCustomerType = (
  gasContainers: Ref<GasContainer[]>,
  meters: Ref<Meter[]>,
) => {
  const hasGasContainers = computed(() => gasContainers.value.length > 0);
  const hasMeters = computed(() => meters.value.length > 0);
  // If there are gas containers, all containers have the same asset type, so it can be checked on the first container
  const assetType = computed(() => gasContainers.value?.[0]?.assetType);

  // --- Unknown Customer ---
  // If there are no gas containers or meters, the customer type is unknown
  const isUnknownCustomer = computed(() => {
    return !hasGasContainers.value && !hasMeters.value;
  });

  // --- METER CUSTOMERS ---
  // A single meter customer has the Z1 asset type of a container
  const isSingleMeterCustomer = computed(() => {
    const hasSingleMeter = assetType.value === MeterAssetType.SingleMeterZ1;
    return hasSingleMeter;
  });

  // A multi meter customer has the Z2 or Z3 asset type of a container
  const isMultiMeterCustomer = computed(() => {
    const hasMultipleMeters =
      assetType.value === MeterAssetType.MultiMeterZ2 ||
      assetType.value === MeterAssetType.MultiMeterZ3;
    return hasMultipleMeters;
  });

  // --- AUTOGAS CUSTOMERS ---
  // An autogas customer has the A1 asset type of a container or has only meters without containers
  const isAutogasCustomer = computed(() => {
    const hasAutogasAsset = assetType.value === MeterAssetType.AutogasA1;
    // In the case of inconsistent data, it is possible that only meters are returned,
    // even though there should actually be an equal number of gas containers and meters.
    const hasMetersOnly = !hasGasContainers.value && hasMeters.value;
    return hasAutogasAsset || hasMetersOnly;
  });

  // --- ORDER CUSTOMERS ---
  const isOrderCustomer = computed(
    () =>
      !isUnknownCustomer.value &&
      !isSingleMeterCustomer.value &&
      !isMultiMeterCustomer.value &&
      !isAutogasCustomer.value,
  );

  // An order customer that has access to bio gas contracts depends on the contract version of the container.
  // This version can differentiate between the containers, therefore all of the containers have to be checked.
  const isBioOrderCustomer = computed(() => {
    if (!hasGasContainers.value) return false;

    const containersWithBioGasContract = gasContainers.value.filter(
      (container) =>
        BIO_GAS_CONTRACT_VERSIONS.includes(
          container.orderContract.version ?? "",
        ),
    );
    return isOrderCustomer.value && containersWithBioGasContract.length > 0;
  });

  const fbsCustomerType = computed(() => {
    if (isUnknownCustomer.value) return FbsCustomerType.UnknownCustomer;

    if (isSingleMeterCustomer.value) return FbsCustomerType.SingleMeterCustomer;

    if (isMultiMeterCustomer.value) return FbsCustomerType.MultiMeterCustomer;

    if (isAutogasCustomer.value) return FbsCustomerType.AutogasCustomer;

    if (isBioOrderCustomer.value) return FbsCustomerType.BioOrderCustomer;

    if (isOrderCustomer.value) return FbsCustomerType.NonBioOrderCustomer;

    return undefined;
  });

  return {
    fbsCustomerType,
  };
};
